import React from 'react';
import { Badge } from '@chakra-ui/react';

/**
 * Centralized status color and label mapping for submissions.
 * Use this everywhere to keep badges consistent.
 */
const SUBMISSION_STATUS_CONFIG = {
  pending: { color: 'yellow', label: 'Pending' },
  in_progress: { color: 'blue', label: 'In Progress' },
  waiting_to_be_received: { color: 'purple', label: 'Waiting to be Received' },
  waiting_on_lab_results: { color: 'teal', label: 'Dropped at Lab' },
  completed: { color: 'green', label: 'Completed' },
  cancelled: { color: 'red', label: 'Cancelled' },
  out_of_area: { color: 'gray', label: 'Out of Area' },
  needs_reschedule: { color: 'orange', label: 'Needs Reschedule' },
  waiting_for_required_info: { color: 'yellow', label: 'Waiting for Required Info' },
};

const PAYMENT_STATUS_CONFIG = {
  paid: { color: 'green', label: 'Paid' },
  unpaid: { color: 'yellow', label: 'Unpaid' },
  failed: { color: 'red', label: 'Failed' },
  refunded: { color: 'orange', label: 'Refunded' },
  pending: { color: 'yellow', label: 'Pending' },
  completed: { color: 'green', label: 'Completed' },
};

const BATCH_STATUS_CONFIG = {
  open: { color: 'yellow', label: 'Open' },
  shipped: { color: 'green', label: 'Shipped' },
  delivered: { color: 'green', label: 'Delivered' },
  pending: { color: 'yellow', label: 'Pending' },
  closed: { color: 'gray', label: 'Closed' },
};

export function getSubmissionStatusColor(status) {
  const key = status?.toLowerCase();
  return SUBMISSION_STATUS_CONFIG[key]?.color || 'gray';
}

export function getSubmissionStatusLabel(status) {
  const key = status?.toLowerCase();
  return SUBMISSION_STATUS_CONFIG[key]?.label || status?.replace(/_/g, ' ') || 'Unknown';
}

export function getPaymentStatusColor(status) {
  const key = status?.toLowerCase();
  return PAYMENT_STATUS_CONFIG[key]?.color || 'gray';
}

export function getPaymentStatusLabel(status) {
  const key = status?.toLowerCase();
  return PAYMENT_STATUS_CONFIG[key]?.label || status || 'Unknown';
}

export function getBatchStatusColor(status) {
  const key = status?.toLowerCase();
  return BATCH_STATUS_CONFIG[key]?.color || 'gray';
}

export function getBatchStatusLabel(status) {
  const key = status?.toLowerCase();
  return BATCH_STATUS_CONFIG[key]?.label || status || 'Unknown';
}

/**
 * Reusable StatusBadge component.
 * @param {string} status - The status string
 * @param {string} type - 'submission' | 'payment' | 'batch'
 */
export function StatusBadge({ status, type = 'submission', ...props }) {
  let color, label;

  switch (type) {
    case 'payment':
      color = getPaymentStatusColor(status);
      label = getPaymentStatusLabel(status);
      break;
    case 'batch':
      color = getBatchStatusColor(status);
      label = getBatchStatusLabel(status);
      break;
    default:
      color = getSubmissionStatusColor(status);
      label = getSubmissionStatusLabel(status);
  }

  return (
    <Badge colorScheme={color} fontSize="xs" px={2} py={1} borderRadius="md" {...props}>
      {label}
    </Badge>
  );
}

export default StatusBadge;

/**
 * Lab-portal timeline state. Derived from existing submission + batch fields
 * — no schema changes required for the initial rollout.
 *
 * State precedence (highest priority first, returns the first matching code):
 *   results_posted   → result file uploaded
 *   waiting_results  → received_at_lab is set (sample with lab, awaiting result post)
 *   in_transit       → batch.shipped_at is set
 *   batched          → batch_id set on submission, batch not yet shipped
 *   drawn            → status indicates the draw is done, no batch yet
 *   scheduled        → blood_collection_time is set
 *   awaiting_info    → some collection details filled but not enough to be Scheduled
 *   order_received   → fallback — lab_id set, nothing else yet
 */
const LAB_TIMELINE_CONFIG = {
  order_received:   { color: 'gray',   label: 'Order Received',     description: 'Order placed — awaiting scheduling.' },
  awaiting_info:    { color: 'yellow', label: 'Awaiting Info',      description: 'Some details missing — admin team is working on it.' },
  contacting:       { color: 'yellow', label: 'Contacting Patient', description: 'Outreach in progress — voicemail / text / email awaiting reply.' },
  scheduled:        { color: 'blue',   label: 'Scheduled',          description: 'Draw scheduled — phleb will arrive at the patient.' },
  en_route:         { color: 'cyan',   label: 'En Route',           description: 'Phleb is on the way to the patient.' },
  rescheduling:     { color: 'red',    label: 'Rescheduling',       description: 'Visit attempted — awaiting reschedule.' },
  drawn:            { color: 'cyan',   label: 'Drawn',              description: 'Blood drawn — being prepped for shipping.' },
  batched:          { color: 'purple', label: 'Batched',            description: 'In a shipping batch — leaving today.' },
  in_transit:       { color: 'orange', label: 'In Transit',         description: 'Shipped — on its way to the lab.' },
  waiting_results:  { color: 'teal',   label: 'Dropped at Lab',     description: 'Specimen dropped at the lab.' },
  results_posted:   { color: 'green',  label: 'Results Posted',     description: 'Results have been posted to the portal.' },
  cancelled:        { color: 'gray',   label: 'Cancelled',          description: 'Draw cancelled — no sample expected.' },
};

// Map an outcome_code (from OUTCOME_CONFIG) → lab-timeline code so the lab
// portal reflects what actually happened, not just whether a date was set.
const OUTCOME_TO_TIMELINE = {
  // Intake
  order_received:        'order_received',
  reviewing_order:       'order_received',
  missing_info:          'awaiting_info',
  // Contact
  voicemail_left:        'contacting',
  no_answer:             'contacting',
  text_sent:             'contacting',
  email_sent:            'contacting',
  reached_patient:       'contacting',
  language_barrier:      'awaiting_info',
  scheduled:             'scheduled',
  appointment_confirmed: 'scheduled',
  reschedule_requested:  'rescheduling',
  // Visit
  en_route:                 'en_route',
  arrived:                  'en_route',
  successful_draw:          'drawn',
  unsuccessful_draw:        'rescheduling',
  partial_draw:             'drawn',
  difficult_stick_completed:'drawn',
  no_show:                  'rescheduling',
  patient_refused:          'cancelled',
  address_wrong:            'awaiting_info',
  patient_unfit:            'rescheduling',
  access_denied:            'rescheduling',
  equipment_failure:        'rescheduling',
  cancelled_by_patient:     'cancelled',
  out_of_area:              'cancelled', // Lincoln: out-of-area = we decline/cancel the order
  // Delivery
  spun:                  'drawn',
  transit_to_dropoff:    'in_transit',
  dropped_off_lab:       'in_transit',
  shipped:               'in_transit',
};

const TIMELINE_ORDER = [
  'order_received',
  'awaiting_info',
  'contacting',
  'scheduled',
  'en_route',
  'rescheduling',
  'drawn',
  'batched',
  'in_transit',
  'waiting_results',
  'results_posted',
  'cancelled',
];

/**
 * Compute the timeline code for a given submission + (optional) batch row.
 *
 * @param {object} submission   — submissions row. Required fields used:
 *   blood_collection_time, status, batch_id, tests_on_the_script, diagnosis,
 *   lab_id, received_at_lab (new in Phase 2, optional)
 * @param {object} [batch]      — submission_batches row matching submission.batch_id.
 *   Optional. Required fields used: shipped_at (Phase 2, optional)
 * @returns {{code: string, label: string, color: string, description: string}}
 */
export function getLabTimelineStatus(submission, batch) {
  if (!submission) return { code: 'order_received', ...LAB_TIMELINE_CONFIG.order_received };

  // Admin can pick a lifecycle code directly from the patient page; that
  // override wins over the field-derived state.
  if (submission.lifecycle_override && LAB_TIMELINE_CONFIG[submission.lifecycle_override]) {
    return {
      code: submission.lifecycle_override,
      ...LAB_TIMELINE_CONFIG[submission.lifecycle_override],
    };
  }

  const submissionStatus = (submission.status || '').toLowerCase();

  // Highest precedence — post-receipt states. If the sample is physically with the lab
  // or further along, that always wins, regardless of any later status flips elsewhere.

  // Results uploaded — represented today by status text or a separate uploaded file.
  // We treat status === 'completed' as a proxy until a dedicated flag exists.
  if (submission.results_posted_at) {
    return { code: 'results_posted', ...LAB_TIMELINE_CONFIG.results_posted };
  }

  // Sample physically with the lab — waiting for them to post results.
  if (submission.received_at_lab) {
    return { code: 'waiting_results', ...LAB_TIMELINE_CONFIG.waiting_results };
  }

  // Batch in transit — phleb/admin marked it shipped.
  if (batch?.shipped_at) {
    return { code: 'in_transit', ...LAB_TIMELINE_CONFIG.in_transit };
  }

  // In a batch but not shipped yet.
  if (submission.batch_id) {
    return { code: 'batched', ...LAB_TIMELINE_CONFIG.batched };
  }

  // The phleb has completed the draw.
  const drawnStatuses = new Set(['completed', 'waiting_to_be_received', 'waiting_on_lab_results']);
  if (drawnStatuses.has(submissionStatus)) {
    return { code: 'drawn', ...LAB_TIMELINE_CONFIG.drawn };
  }

  // Outcome the phleb / admin logged via OutcomeDrawer — promote it into the
  // lab timeline. We do this BEFORE the field-derived "scheduled" check below
  // so a row that has a blood_collection_time AND a 'no_show' outcome doesn't
  // still show as Scheduled.
  if (submission.outcome_code && OUTCOME_TO_TIMELINE[submission.outcome_code]) {
    const mapped = OUTCOME_TO_TIMELINE[submission.outcome_code];
    if (LAB_TIMELINE_CONFIG[mapped]) {
      return { code: mapped, ...LAB_TIMELINE_CONFIG[mapped] };
    }
  }

  // Submission status flagged cancelled, or out-of-area (a decline, which the
  // lab timeline shows as cancelled — Lincoln just needs "won't be fulfilled").
  if (submissionStatus === 'cancelled' || submissionStatus === 'out_of_area') {
    return { code: 'cancelled', ...LAB_TIMELINE_CONFIG.cancelled };
  }

  // Post-attempt branch states — visit was tried but didn't produce a sample yet.
  // These come AFTER the post-receipt checks above so a re-draw cycle (received once,
  // then flagged needs_reschedule for a second draw) doesn't visually regress.
  if (submissionStatus === 'needs_reschedule') {
    return { code: 'rescheduling', ...LAB_TIMELINE_CONFIG.rescheduling };
  }
  if (submissionStatus === 'waiting_for_required_info') {
    return { code: 'awaiting_info', ...LAB_TIMELINE_CONFIG.awaiting_info };
  }

  // Scheduled — collection time set.
  if (submission.blood_collection_time) {
    return { code: 'scheduled', ...LAB_TIMELINE_CONFIG.scheduled };
  }

  // Some details filled (tests OR diagnosis) but no collection time yet.
  const hasTests = Array.isArray(submission.tests_on_the_script)
    ? submission.tests_on_the_script.length > 0
    : !!submission.tests_on_the_script;
  const hasDiagnosis = !!submission.diagnosis;
  if (hasTests || hasDiagnosis) {
    return { code: 'awaiting_info', ...LAB_TIMELINE_CONFIG.awaiting_info };
  }

  return { code: 'order_received', ...LAB_TIMELINE_CONFIG.order_received };
}

/**
 * Returns the configuration for a known timeline code (no submission required).
 * Useful for legends, filter pills, and the timeline drawer.
 */
export function getLabTimelineConfig(code) {
  return LAB_TIMELINE_CONFIG[code] || LAB_TIMELINE_CONFIG.order_received;
}

/**
 * Ordered list of timeline codes, earliest stage first.
 * Useful for rendering the full timeline drawer in order.
 */
export function getLabTimelineOrder() {
  return [...TIMELINE_ORDER];
}

/**
 * Visit-progress catalog. Every status the team can pick for a draw, from the
 * moment the order is received until the specimen is dropped at the lab.
 *
 * Each entry maps to:
 *   phase            — 'intake' | 'contact' | 'visit' | 'delivery'  (groups the drawer)
 *   nextStatus       — the submissions.status the row should move to
 *   followupRequired — whether admin needs to do something (drives the "needs attention" queue)
 *   needsNote        — whether the drawer should prompt for a free-text note before saving
 */
const OUTCOME_CONFIG = {
  // ── Phase 1: Intake ─────────────────────────────────────────────────────
  order_received: {
    label: 'Order Received',
    description: 'Order received from the lab/doctor and acknowledged.',
    color: 'gray',
    icon: 'inbox',
    phase: 'intake',
    nextStatus: 'pending',
    followupRequired: false,
    needsNote: false,
  },
  reviewing_order: {
    label: 'Reviewing Order',
    description: 'Order is being checked for completeness (tests, script, ICD codes).',
    color: 'gray',
    icon: 'review',
    phase: 'intake',
    nextStatus: 'pending',
    followupRequired: false,
    needsNote: false,
  },
  missing_info: {
    label: 'Missing Info',
    description: 'Order needs more details (insurance, script, etc.) before scheduling.',
    color: 'yellow',
    icon: 'info',
    phase: 'intake',
    nextStatus: 'waiting_for_required_info',
    followupRequired: true,
    needsNote: true,
  },
  out_of_area: {
    label: 'Out of Coverage Area',
    description: 'Patient address is outside our service region — cannot fulfill. Marks the draw so no one contacts them.',
    color: 'gray',
    icon: 'map',
    phase: 'intake',
    // Its own terminal status (distinct from Cancelled). Still listed in
    // TERMINAL_CANCEL_OUTCOMES so the draw drops out of every call/available
    // list, but reads as "Out of Area" everywhere and maps to a cancelled push
    // to Lincoln (they just need to know we can't fulfill it).
    nextStatus: 'out_of_area',
    followupRequired: false,
    needsNote: false,
  },

  // ── Phase 2: Patient Contact ────────────────────────────────────────────
  voicemail_left: {
    label: 'Voicemail Left',
    description: 'Called the patient and left a voicemail — awaiting callback.',
    color: 'yellow',
    icon: 'voicemail',
    phase: 'contact',
    nextStatus: 'in_progress',
    followupRequired: true,
    needsNote: false,
  },
  no_answer: {
    label: 'No Answer',
    description: 'Called — phone rang out, no voicemail option.',
    color: 'yellow',
    icon: 'no-answer',
    phase: 'contact',
    nextStatus: 'in_progress',
    followupRequired: true,
    needsNote: false,
  },
  text_sent: {
    label: 'Text Sent',
    description: 'SMS sent to the patient — awaiting reply.',
    color: 'yellow',
    icon: 'sms',
    phase: 'contact',
    nextStatus: 'in_progress',
    followupRequired: true,
    needsNote: false,
  },
  email_sent: {
    label: 'Email Sent',
    description: 'Email sent to the patient — awaiting reply.',
    color: 'yellow',
    icon: 'email',
    phase: 'contact',
    nextStatus: 'in_progress',
    followupRequired: true,
    needsNote: false,
  },
  reached_patient: {
    label: 'Reached Patient',
    description: 'Spoke with the patient on the phone.',
    color: 'cyan',
    icon: 'phone',
    phase: 'contact',
    nextStatus: 'in_progress',
    followupRequired: false,
    needsNote: false,
  },
  language_barrier: {
    label: 'Language Barrier',
    description: 'Patient does not speak English well — need translator or different language.',
    color: 'orange',
    icon: 'language',
    phase: 'contact',
    nextStatus: 'waiting_for_required_info',
    followupRequired: true,
    needsNote: true,
  },
  scheduled: {
    label: 'Appointment Scheduled',
    description: 'Date and time confirmed with the patient.',
    color: 'blue',
    icon: 'calendar',
    phase: 'contact',
    nextStatus: 'in_progress',
    followupRequired: false,
    needsNote: false,
  },
  appointment_confirmed: {
    label: 'Confirmed',
    description: 'Patient confirmed the upcoming appointment.',
    color: 'blue',
    icon: 'confirm',
    phase: 'contact',
    nextStatus: 'in_progress',
    followupRequired: false,
    needsNote: false,
  },
  reschedule_requested: {
    label: 'Reschedule Requested',
    description: 'Patient asked to move the appointment.',
    color: 'orange',
    icon: 'reschedule',
    phase: 'contact',
    nextStatus: 'needs_reschedule',
    followupRequired: true,
    needsNote: false,
  },

  // ── Phase 3: At Visit ───────────────────────────────────────────────────
  en_route: {
    label: 'En Route',
    description: 'Phleb is on the way to the patient.',
    color: 'cyan',
    icon: 'route',
    phase: 'visit',
    nextStatus: 'in_progress',
    followupRequired: false,
    needsNote: false,
  },
  arrived: {
    label: 'Arrived',
    description: 'Phleb arrived at the patient location.',
    color: 'cyan',
    icon: 'pin',
    phase: 'visit',
    nextStatus: 'in_progress',
    followupRequired: false,
    needsNote: false,
  },
  successful_draw: {
    label: 'Successful Draw',
    description: 'Sample collected. Add any attributes (difficult stick, partial, etc.) below.',
    color: 'green',
    icon: 'check',
    phase: 'visit',
    nextStatus: 'completed',
    followupRequired: false,
    needsNote: false,
    supportsFlags: true,
  },
  unsuccessful_draw: {
    label: 'Unsuccessful Draw',
    description: 'No sample collected — draw needs to be rescheduled. Add attributes below.',
    color: 'red',
    icon: 'syringe',
    phase: 'visit',
    nextStatus: 'needs_reschedule',
    followupRequired: true,
    needsNote: false,
    supportsFlags: true,
  },
  // Legacy outcomes — no longer offered in the picker (replaced by
  // successful_draw / unsuccessful_draw + the 'difficult_stick' / 'partial_draw'
  // attribute flags), but kept here so historical records still render.
  partial_draw: {
    label: 'Partial Draw',
    description: 'Some tubes drawn, some failed. Lab will decide what is runnable.',
    color: 'yellow',
    icon: 'partial',
    phase: 'visit',
    nextStatus: 'completed',
    followupRequired: true,
    needsNote: true,
  },
  difficult_stick_completed: {
    label: 'Difficult Stick',
    description: 'Successful draw, but multiple sticks were required. Flag for review.',
    color: 'cyan',
    icon: 'syringe',
    phase: 'visit',
    nextStatus: 'completed',
    followupRequired: false,
    needsNote: false,
  },
  no_show: {
    label: 'No-Show',
    description: 'Patient not home / not answering after the wait window.',
    color: 'orange',
    icon: 'no-show',
    phase: 'visit',
    nextStatus: 'needs_reschedule',
    followupRequired: true,
    needsNote: false,
  },
  patient_refused: {
    label: 'Patient Refused',
    description: 'Patient was home but declined the draw (afraid of needles, etc.).',
    color: 'red',
    icon: 'refused',
    phase: 'visit',
    nextStatus: 'cancelled',
    followupRequired: true,
    needsNote: false,
  },
  address_wrong: {
    label: 'Wrong Address',
    description: 'Address does not exist / wrong unit / patient moved.',
    color: 'red',
    icon: 'map',
    phase: 'visit',
    nextStatus: 'waiting_for_required_info',
    followupRequired: true,
    needsNote: true,
  },
  patient_unfit: {
    label: 'Patient Unfit',
    description: 'Patient is too ill, fainting risk, or violated fast — needs reschedule.',
    color: 'orange',
    icon: 'unfit',
    phase: 'visit',
    nextStatus: 'needs_reschedule',
    followupRequired: true,
    needsNote: true,
  },
  access_denied: {
    label: 'Access Denied',
    description: 'Building / gate / security blocked entry.',
    color: 'red',
    icon: 'lock',
    phase: 'visit',
    nextStatus: 'needs_reschedule',
    followupRequired: true,
    needsNote: true,
  },
  equipment_failure: {
    label: 'Equipment Failure',
    description: 'Phleb missing supplies or device failed.',
    color: 'purple',
    icon: 'tools',
    phase: 'visit',
    nextStatus: 'needs_reschedule',
    followupRequired: true,
    needsNote: false,
  },
  cancelled_by_patient: {
    label: 'Patient Cancelled',
    description: 'Patient called or texted to cancel before the visit.',
    color: 'gray',
    icon: 'cancel',
    phase: 'visit',
    nextStatus: 'cancelled',
    followupRequired: false,
    needsNote: false,
  },

  // ── Phase 4: Delivery ───────────────────────────────────────────────────
  spun: {
    label: 'Spun — Ready for Drop',
    description: 'Blood spun and the labeled specimen photographed. Ready to drop at the lab.',
    color: 'cyan',
    icon: 'tools',
    phase: 'delivery',
    nextStatus: 'completed',
    followupRequired: false,
    needsNote: false,
  },
  transit_to_dropoff: {
    label: 'In Transit to Drop-off',
    description: 'Specimen drawn — phleb is on the way to drop it at the lab.',
    color: 'orange',
    icon: 'route',
    phase: 'delivery',
    nextStatus: 'completed',
    followupRequired: false,
    needsNote: false,
  },
  dropped_off_lab: {
    label: 'Dropped at Lab',
    description: 'Specimen physically dropped off at the lab.',
    color: 'green',
    icon: 'drop',
    phase: 'delivery',
    nextStatus: 'completed',
    followupRequired: false,
    needsNote: false,
  },
  shipped: {
    label: 'Shipped to Lab',
    description: 'Specimen shipped (FedEx/courier) to the lab.',
    color: 'green',
    icon: 'ship',
    phase: 'delivery',
    nextStatus: 'completed',
    followupRequired: false,
    needsNote: false,
  },

  // System events, not dispositions. Deliberately absent from OUTCOME_ORDER so
  // they never appear in the OutcomeDrawer picker (getAllOutcomes maps
  // OUTCOME_ORDER), but getOutcomeConfig still resolves them so the activity
  // timeline can render a labelled badge. phase 'system' keeps them out of
  // getOutcomePhases() too. Written only to submission_status_events by
  // logAssignmentChange — never to submissions.status/outcome_code, so they
  // don't affect the current-status card or the LDX lifecycle.
  assigned: {
    label: 'Assigned',
    description: 'Draw assigned to a phlebotomist.',
    color: 'green',
    icon: 'phone',
    phase: 'system',
    nextStatus: null,
    followupRequired: false,
    needsNote: false,
  },
  unassigned: {
    label: 'Unassigned',
    description: 'Draw removed from a phlebotomist.',
    color: 'gray',
    icon: 'info',
    phase: 'system',
    nextStatus: null,
    followupRequired: false,
    needsNote: false,
  },
};

const OUTCOME_ORDER = [
  // Intake
  'order_received', 'reviewing_order', 'missing_info', 'out_of_area',
  // Contact
  'voicemail_left', 'no_answer', 'text_sent', 'email_sent', 'reached_patient',
  'language_barrier', 'scheduled', 'appointment_confirmed', 'reschedule_requested',
  // Visit
  'en_route', 'arrived',
  'successful_draw', 'unsuccessful_draw',
  'no_show', 'patient_refused', 'address_wrong', 'patient_unfit',
  'access_denied', 'equipment_failure', 'cancelled_by_patient',
  // Delivery
  'spun', 'transit_to_dropoff', 'dropped_off_lab', 'shipped',
];

const PHASE_LABELS = {
  intake:   'Intake',
  contact:  'Patient Contact',
  visit:    'At Visit',
  delivery: 'Delivery',
};

export function getOutcomePhaseLabel(phase) {
  return PHASE_LABELS[phase] || phase;
}

export function getOutcomePhases() {
  return ['intake', 'contact', 'visit', 'delivery'];
}

export function getOutcomeConfig(code) {
  return OUTCOME_CONFIG[code] || null;
}

// ── Outcome attribute flags ────────────────────────────────────────────────
// Independent of the result (successful vs unsuccessful) — a draw can be both
// successful AND a difficult stick, etc. Multi-select; shown on the detail step
// of any outcome whose config has supportsFlags. Stored in submissions.outcome_flags.
const OUTCOME_FLAGS = [
  { key: 'difficult_stick',   label: 'Difficult stick',     color: 'cyan' },
  { key: 'partial_draw',      label: 'Partial draw',        color: 'yellow' },
  { key: 'multiple_attempts', label: 'Multiple attempts',   color: 'purple' },
  { key: 'hematoma',          label: 'Hematoma / bruising', color: 'orange' },
];

export function getOutcomeFlags() {
  return OUTCOME_FLAGS.map((f) => ({ ...f }));
}

export function getOutcomeFlagConfig(key) {
  return OUTCOME_FLAGS.find((f) => f.key === key) || null;
}

export function getOutcomeFlagLabel(key) {
  return getOutcomeFlagConfig(key)?.label || key;
}

export function getOutcomeOrder() {
  return [...OUTCOME_ORDER];
}

export function getAllOutcomes() {
  return OUTCOME_ORDER.map((code) => ({ code, ...OUTCOME_CONFIG[code] }));
}
